import { Text, Tabs, Textarea, Grid, LoadingOverlay, ActionIcon, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Note } from 'components/Note/Note';
import { usePatientLogs } from 'hooks/api/useProfile';
import { usePatient } from 'hooks/api/useUser';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { addGroupLogging, deleteGroupLogging, updateGroupLogging } from 'services/profile';
import { PatientIdProps } from 'models/general';
import { BsPlus } from 'react-icons/bs';
import { DEFAULT_ICON_SIZE } from 'utils/general';
import { queryKeys } from 'utils/query';

export const Notes = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();

  const { data: patientData } = usePatient({ patientId });

  const patientActiveGroups = patientData?.visibleEnlistments;
  const [tabGroupId, setTabGroupId] = useState<string | undefined>(
    patientActiveGroups ? patientActiveGroups[0]?.groupId : undefined,
  );
  const [notesText, setNotesText] = useState<string>('');

  const { data: groupNotes, isLoading } = usePatientLogs({ profileId: patientId, groupId: tabGroupId });

  const { mutate: addLog } = useCustomMutation({
    fn: addGroupLogging,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.groupLogging]],
      callback: () => setNotesText(''),
    },
  });
  const { mutate: updateLog } = useCustomMutation({
    fn: updateGroupLogging,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.groupLogging]],
    },
  });
  const { mutate: deleteLog } = useCustomMutation({
    fn: deleteGroupLogging,
    onSuccess: {
      invalidateQueryKeys: [[queryKeys.groupLogging]],
    },
  });

  const handleAddLogging = () => addLog({ profileId: patientId, groupId: tabGroupId, text: notesText });
  const handleUpdateLogging = (entryId: string, newNote: string) => {
    updateLog({ profileId: patientId, groupId: tabGroupId, entryId, text: newNote });
  };
  const handleDeleteLogging = async (entryId: string) => {
    deleteLog({ profileId: patientId, groupId: tabGroupId, entryId });
  };

  return (
    <Tabs variant={'outline'} onChange={(tab) => tab && setTabGroupId(tab)} defaultValue={tabGroupId}>
      <Tabs.List>
        {patientActiveGroups?.map((item) => (
          <Tabs.Tab key={item.name} value={item.groupId}>
            <Text maw={170}>{item.name}</Text>
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {!isLoading ? (
        patientActiveGroups?.map((item) => (
          <Tabs.Panel key={`panel-${item.groupId}`} value={item.groupId}>
            <Grid align={'center'}>
              <Grid.Col span={11}>
                <Textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  my={'lg'}
                  autosize
                  minRows={2}
                  placeholder={t('patients_add_logging')}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Tooltip label={`${t('patients_add_logging')}`}>
                  <ActionIcon
                    variant={'filled'}
                    size={'lg'}
                    color={'var(--mantine-color-blue-5)'}
                    disabled={!notesText}
                    onClick={() => handleAddLogging()}
                  >
                    <BsPlus size={DEFAULT_ICON_SIZE} />
                  </ActionIcon>
                </Tooltip>
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={12}>
                {groupNotes && groupNotes.length > 0 ? (
                  groupNotes.map((note) => (
                    <Note
                      key={note.id}
                      id={note?.id}
                      text={note.text || ''}
                      updateTimestamp={note.updateTimestamp}
                      author={note?.userId || ''}
                      editTooltipLabel={t('patients_edit_logging')}
                      deleteTooltipLabel={t('patients_delete_logging')}
                      handleDeleteLogging={handleDeleteLogging}
                      handleUpdateLogging={handleUpdateLogging}
                    />
                  ))
                ) : (
                  <Text>{t('patients_no_logging')}</Text>
                )}
              </Grid.Col>
            </Grid>
          </Tabs.Panel>
        ))
      ) : (
        <LoadingOverlay visible={true} />
      )}
    </Tabs>
  );
};
